import React from 'react'
import { InfoCircle } from 'iconsax-react'
import { useTheme } from '@mui/material'

import {
  checkPermission,
  getPushSubscription,
  isSupported
} from '@SonarCX/setupWorker'
import { subscribePush } from '@SonarCX/lib/api'
import { useAuth } from '@SonarCX/providers/AuthProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { toggleConsent } from '@SonarCX/slices/alert'
import useSavedValue from '@SonarCX/hooks/useSavedValue'

import useStyles from './styles'

const CloseIcon: React.FC<React.HTMLAttributes<SVGSVGElement>> = props => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <path
      d='M18 6L6 18'
      stroke='#BF8A00'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M6 6L18 18'
      stroke='#BF8A00'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const NotificationConsentBanner: React.FC = () => {
  const theme = useTheme()

  const { isLoggedIn } = useAuth()
  const { isSmall } = useBreakpoint()
  const dispatch = useAppDispatch()
  const [visible, setVisible] = React.useState(
    isSupported() && Notification.permission === 'default'
  )
  const [checking, setChecking] = React.useState(
    isSupported() && Notification.permission === 'granted'
  )
  const [done, setDone] = useSavedValue({
    key: 'notification-done',
    defaultValue: 'false',
    useSession: true
  })
  const [beenDismissed, setBeenDismissed] = useSavedValue({
    key: 'notification-dismissed',
    defaultValue: 'false',
    useSession: true
  })
  const classes = useStyles()

  React.useEffect(() => {
    if (!checking || done === 'true' || !isLoggedIn) {
      return
    }

    const handleSetup = async () => {
      const allowed = await checkPermission()

      setVisible(false)

      if (allowed) {
        const subscription = await getPushSubscription()

        if (subscription) {
          await subscribePush(subscription)
          setDone('true')
        }
      }
    }

    handleSetup()
      .then(() => setChecking(false))
      .catch(err => {
        console.error('Error setting up notifications', err)
      })
  }, [checking, done, isLoggedIn, setDone])

  const startPermissionCheck = React.useCallback(() => {
    setChecking(true)
  }, [])

  const handleClose = React.useCallback(
    e => {
      e.stopPropagation()
      setBeenDismissed('true')
      setVisible(false)
    },
    [setBeenDismissed]
  )

  React.useEffect(() => {
    if (!visible || !isLoggedIn || isSmall) {
      dispatch(toggleConsent(false))
      return
    }
    dispatch(toggleConsent(visible))
  }, [visible, isSmall, isLoggedIn, dispatch])

  if (!visible || !isLoggedIn || isSmall || beenDismissed === 'true') {
    return null
  }

  return (
    <div className={classes.root} data-testid='notifications-top-bar'>
      <div className={classes.text}>
        <InfoCircle
          variant='Bold'
          size={theme.size.lg}
          color='#BF8A00'
          className='icon'
        />
        Desktop notifications are not yet enabled.
        <span
          onClick={startPermissionCheck}
          className={classes.link}
          data-testid='notifications-top-bar-enable-now-cta'
        >
          Enable now
        </span>
      </div>

      <CloseIcon
        data-testid='hide-notif'
        onClick={handleClose}
        className={classes.close}
      />
    </div>
  )
}

export default NotificationConsentBanner
